import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from './Sidebar';
import Header from './Header';
import './StatusPengiriman.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:5000';

function StatusPengiriman({ user, onLogout }) {
  const navigate = useNavigate();
  const [pengirimanList, setPengirimanList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isAuthenticatedRef = useRef(false);
  const MAX_RECONNECT_ATTEMPTS = 5;

  const [showModal, setShowModal] = useState(false);
  const [selectedPengiriman, setSelectedPengiriman] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [dokumentasiFile, setDokumentasiFile] = useState(null);

  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    status: 'dalamproses',
    lokasi: '',
    tanggal: new Date().toISOString().slice(0, 16)
  });

  const fetchPengiriman = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/pengiriman`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPengirimanList(response.data.data || []);
      setFilteredList(response.data.data || []);
    } catch (err) {
      setError('Gagal memuat data pengiriman');
      console.error('Error fetching pengiriman:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('No token found, skipping WebSocket connection');
      return;
    }

    if (wsRef.current) {
      const state = wsRef.current.readyState;
      if (state === WebSocket.CONNECTING || state === WebSocket.OPEN) {
        console.log('WebSocket already connected or connecting');
        return;
      }
    }

    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      console.log('Max reconnection attempts reached');
      return;
    }

    try {
      const ws = new WebSocket(WS_URL);

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0;
        isAuthenticatedRef.current = false;
        ws.send(JSON.stringify({ type: 'authenticate', token }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'authenticated') {
            isAuthenticatedRef.current = true;
          }
        } catch (err) {
          console.error('Error parsing message:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = (event) => {
        console.log(`WebSocket closed with code: ${event.code}`);
        wsRef.current = null;
        isAuthenticatedRef.current = false;

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }

        const shouldReconnect =
          event.code !== 1000 &&
          localStorage.getItem('token') &&
          reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS;

        if (shouldReconnect) {
          reconnectAttemptsRef.current += 1;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 10000);
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})...`);

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            connectWebSocket();
          }, delay);
        }
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('WebSocket init error:', err);
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    fetchPengiriman();

    const wsTimeout = setTimeout(() => {
      connectWebSocket();
    }, 500);

    return () => {
      clearTimeout(wsTimeout);

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (wsRef.current) {
        const ws = wsRef.current;
        ws.onclose = null;
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close(1000, 'Component unmounted');
        }
        wsRef.current = null;
      }

      reconnectAttemptsRef.current = 0;
      isAuthenticatedRef.current = false;
    };
  }, [fetchPengiriman, connectWebSocket]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredList(pengirimanList);
    } else {
      const filtered = pengirimanList.filter(item =>
        item.no_resi.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.nama_pengirim.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.nama_penerima.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredList(filtered);
    }
  }, [searchTerm, pengirimanList]);

  const fetchStatusHistory = async (noResi) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/status-pengiriman/${noResi}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatusHistory(response.data.data || []);
    } catch (err) {
      console.error('Error fetching status history:', err);
    }
  };

  const handleCameraCapture = (e) => {
    const file = e.target.files[0];

    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Ukuran file maksimal 5MB');
        e.target.value = '';
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif'];
      const fileTypeLower = file.type.toLowerCase();

      if (!allowedTypes.includes(fileTypeLower)) {
        setError(`Format file tidak didukung: ${file.type}. Gunakan jpg, png, gif, atau webp`);
        e.target.value = '';
        return;
      }

      setDokumentasiFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        setError('Gagal membaca file');
      };
      reader.readAsDataURL(file);

      setSuccess('Foto berhasil diambil!');
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const triggerCamera = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleOpenModal = (pengiriman) => {
    setSelectedPengiriman(pengiriman);
    setShowModal(true);
    setFormData({
      status: 'dalamproses',
      lokasi: '',
      tanggal: new Date().toISOString().slice(0, 16)
    });
    setDokumentasiFile(null);
    setImagePreview(null);
    fetchStatusHistory(pengiriman.no_resi);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPengiriman(null);
    setStatusHistory([]);
    setFormData({
      status: 'dalamproses',
      lokasi: '',
      tanggal: new Date().toISOString().slice(0, 16)
    });
    setDokumentasiFile(null);
    setImagePreview(null);
    setError('');
    setSuccess('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Ukuran file maksimal 5MB');
        e.target.value = '';
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Hanya file gambar yang diperbolehkan (jpg, jpeg, png, gif, webp)');
        e.target.value = '';
        return;
      }

      setDokumentasiFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteStatus = async (statusId) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus status ini?')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/status-pengiriman/delete/${statusId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Status pengiriman berhasil dihapus!');
      fetchStatusHistory(selectedPengiriman.no_resi);
      fetchPengiriman();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal menghapus status');
      console.error('Error deleting status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setDokumentasiFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleAddStatus = async (e) => {
    e.preventDefault();

    if (!formData.status) {
      setError('Status harus diisi');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');

      const formDataToSend = new FormData();
      formDataToSend.append('no_resi', selectedPengiriman.no_resi);
      formDataToSend.append('status', formData.status);
      formDataToSend.append('lokasi', formData.lokasi);
      formDataToSend.append('tanggal', formData.tanggal);

      if (dokumentasiFile) {
        formDataToSend.append('dokumentasi', dokumentasiFile);
      }

      await axios.post(
        `${API_URL}/api/status-pengiriman`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setSuccess('Status pengiriman berhasil ditambahkan!');

      setFormData({
        status: 'dalamproses',
        lokasi: '',
        tanggal: new Date().toISOString().slice(0, 16)
      });
      setDokumentasiFile(null);
      setImagePreview(null);

      if (fileInputRef.current) fileInputRef.current.value = '';

      fetchStatusHistory(selectedPengiriman.no_resi);
      fetchPengiriman();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal menambahkan status');
      console.error('Error adding status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      dalamproses: { label: 'Dalam Proses', class: 'status-dalam-proses', icon: 'bi-hourglass-split' },
      dikirim: { label: 'Dikirim', class: 'status-dikirim', icon: 'bi-truck' },
      diterima: { label: 'Diterima', class: 'status-diterima', icon: 'bi-check-circle-fill' }
    };
    const config = statusConfig[status] || statusConfig.dalamproses;
    return (
      <span className={`status-badge ${config.class}`}>
        <i className={`bi ${config.icon} me-1`}></i>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('id-ID', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleNavigate = (page) => {
    switch (page) {
      case 'dashboard':
        navigate('/dashboard');
        break;
      case 'pengiriman':
        navigate('/pengiriman');
        break;
      case 'status-pengiriman':
        navigate('/status-pengiriman');
        break;
      case 'kelolaakun':
        navigate('/kelolaakun');
        break;
      default:
        navigate('/dashboard');
    }
  };

  const handleToggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <>
      <Header user={user} onMenuToggle={handleToggleSidebar} />
      <div className="dashboard-layout" style={{ marginTop: '60px' }}>
        <Sidebar
          user={user}
          onLogout={onLogout}
          currentPage="status-pengiriman"
          onNavigate={handleNavigate}
          isOpen={sidebarOpen}
          onToggle={handleToggleSidebar}
        />
        <div className="dashboard-main">
          <div className="dashboard-container">
            <div className="status-pengiriman-page">
              <div className="page-header">
                <h1>
                  <i className="bi bi-clipboard-check me-2"></i>
                  Status Pengiriman
                </h1>
                <p>Kelola status pengiriman berdasarkan nomor resi</p>
              </div>

              {success && (
                <div className="success-message">
                  <i className="bi bi-check-circle me-2"></i>
                  {success}
                </div>
              )}
              {error && (
                <div className="error-message">
                  <i className="bi bi-x-circle me-2"></i>
                  {error}
                </div>
              )}

              <div className="table-wrapper">
                <div className="table-controls">
                  <h2>Daftar Status Pengiriman</h2>
                  <div className="search-box">
                    <i className="bi bi-search search-icon"></i>
                    <input
                      type="text"
                      placeholder="Cari no resi"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="search-input"
                    />
                  </div>
                </div>

                <div className="table-container">
                  {loading ? (
                    <div className="loading-state">
                      <i className="bi bi-hourglass-split me-2"></i>
                      <p>Memuat data...</p>
                    </div>
                  ) : filteredList.length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-icon">
                        <i className="bi bi-box-seam" style={{ fontSize: '48px' }}></i>
                      </div>
                      <h3>Tidak ada data pengiriman</h3>
                      <p>Belum ada data pengiriman yang tersedia</p>
                    </div>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>No</th>
                          <th>No Resi</th>
                          <th>Pengirim</th>
                          <th>Penerima</th>
                          <th>Tujuan</th>
                          <th>Tanggal</th>
                          <th>Status</th>
                          <th>Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredList.map((item, index) => (
                          <tr key={item.id}>
                            <td>{index + 1}</td>
                            <td><strong>{item.no_resi}</strong></td>
                            <td>{item.nama_pengirim}</td>
                            <td>{item.nama_penerima}</td>
                            <td>{item.alamat_tujuan}</td>
                            <td>{formatDate(item.tanggal)}</td>
                            <td>{getStatusBadge(item.status)}</td>
                            <td>
                              <button
                                className="btn-action"
                                onClick={() => handleOpenModal(item)}
                                title="Tambah Status"
                              >
                                <i className="bi bi-plus-circle me-1"></i>
                                Tambah Status
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && selectedPengiriman && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="bi bi-plus-circle me-2"></i>
                Tambah Status Pengiriman
              </h2>
              <button className="modal-close" onClick={handleCloseModal}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="pengiriman-info">
                <h3>
                  <i className="bi bi-box-seam me-2"></i>
                  Informasi Pengiriman
                </h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">No Resi:</span>
                    <span className="info-value"><strong>{selectedPengiriman.no_resi}</strong></span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Status Terakhir:</span>
                    <span className="info-value">{getStatusBadge(selectedPengiriman.status)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Pengirim:</span>
                    <span className="info-value">{selectedPengiriman.nama_pengirim}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Penerima:</span>
                    <span className="info-value">{selectedPengiriman.nama_penerima}</span>
                  </div>
                </div>
              </div>

              <form onSubmit={handleAddStatus} className="status-form">
                <h3>
                  <i className="bi bi-pencil-square me-2"></i>
                  Form Tambah Status
                </h3>

                <div className="form-row">
                  <div className="form-group">
                    <label>Status Pengiriman *</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      required
                      className="form-select"
                    >
                      <option value="dalamproses">
                        ⏳ Dalam Proses
                      </option>
                      <option value="dikirim">
                        🚚 Dikirim
                      </option>
                      <option value="diterima">
                        ✅ Diterima
                      </option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Tanggal & Waktu *</label>
                    <input
                      type="datetime-local"
                      name="tanggal"
                      value={formData.tanggal}
                      onChange={handleChange}
                      required
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Lokasi Saat Ini</label>
                  <input
                    type="text"
                    name="lokasi"
                    value={formData.lokasi}
                    onChange={handleChange}
                    placeholder="Contoh: Surabaya - Terminal Cargo"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Foto Dokumentasi</label>

                  <div className="camera-upload-buttons">
                    <button
                      type="button"
                      onClick={triggerCamera}
                      className="btn-camera"
                    >
                      <i className="bi bi-camera-fill"></i>
                      Ambil Foto
                    </button>

                    <input
                      ref={fileInputRef}
                      type="file"
                      name="dokumentasi"
                      onChange={handleCameraCapture}
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      capture="environment"
                      style={{ display: 'none' }}
                    />

                    <label
                      htmlFor="file-upload-gallery"
                      className="btn-upload-label"
                    >
                      <i className="bi bi-upload"></i>
                      <span>Upload dari Galeri</span>
                    </label>
                    <input
                      id="file-upload-gallery"
                      type="file"
                      name="dokumentasi_gallery"
                      onChange={handleFileChange}
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      style={{ display: 'none' }}
                    />
                  </div>

                  <small className="form-help">Ambil foto langsung dengan kamera atau upload dari galeri (jpg, png, gif, webp - max 5MB)</small>

                  {imagePreview && (
                    <div className="image-preview-container">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="preview-image"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="btn-remove-image"
                      >
                        <i className="bi bi-trash3"></i>
                        Hapus Foto
                      </button>
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                    Batal
                  </button>
                  <button type="submit" className="btn-submit" disabled={loading}>
                    {loading ? (
                      <>
                        <i className="bi bi-hourglass-split me-2"></i>
                        Menyimpan...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-floppy me-2"></i>
                        Simpan Status
                      </>
                    )}
                  </button>
                </div>
              </form>

              {statusHistory.length > 0 && (
                <div className="status-history-modal">
                  <h3>
                    <i className="bi bi-clock-history me-2"></i>
                    Riwayat Status ({statusHistory.length})
                  </h3>
                  <div className="timeline-modal">
                    {statusHistory.map((item, index) => (
                      <div key={item.id} className="timeline-item-modal">
                        <div className="timeline-marker-modal">
                          {index === 0 ? (
                            <i className="bi bi-circle-fill" style={{ color: '#007bff' }}></i>
                          ) : (
                            <i className="bi bi-circle" style={{ color: '#ccc' }}></i>
                          )}
                        </div>
                        <div className="timeline-content-modal">
                          <div className="timeline-header-modal" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              {getStatusBadge(item.status)}
                              <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => handleDeleteStatus(item.id)}
                                disabled={loading}
                                title="Hapus status"
                              >
                                <i className="bi bi-trash3"></i>
                              </button>
                            </div>
                            <span className="timeline-date-modal">
                              {formatDateTime(item.tanggal)}
                            </span>
                          </div>
                          {item.lokasi && (
                            <p className="timeline-lokasi-modal">
                              <i className="bi bi-geo-alt me-1"></i>
                              {item.lokasi}
                            </p>
                          )}
                          {item.dokumentasi && (
                            <div className="timeline-docs-modal">
                              <img
                                src={`${API_URL}${item.dokumentasi}`}
                                alt="Dokumentasi"
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '200px',
                                  borderRadius: '8px',
                                  marginTop: '8px',
                                  cursor: 'pointer',
                                  border: '2px solid #ddd'
                                }}
                                onClick={() => window.open(`${API_URL}${item.dokumentasi}`, '_blank')}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default StatusPengiriman;